import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Volume2, VolumeX, Smile, LogIn, Compass, RefreshCw } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";
import { AuthContext } from "@/context/AuthContext";
import ChatAnimations from "@/components/chat/ChatAnimations";
import GraduationCard from "@/components/chat/GraduationCard";
import BreakCard from "@/components/chat/BreakCard";
import ChatInput from "@/components/chat/ChatInput";
import ChatHeader from "@/components/chat/ChatHeader";
import ChatMessages from "@/components/chat/ChatMessages";
import RoadAheadCard from "@/components/RoadAheadCard";
import ActionItemsCard, { parseActionItems } from "@/components/chat/ActionItemsCard";
import OutfitProgressBar from "@/components/chat/OutfitProgressBar";
import useTTS from "@/components/chat/useTTS";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sticker sets for Papaya
const STICKER_CATEGORIES = [
  {
    name: 'Papaya',
    stickers: [
      { id: 'bunny-love', emoji: '\uD83D\uDC30\u2764\uFE0F', label: 'Bunny Love' },
      { id: 'sparkle', emoji: '\u2728', label: 'Sparkle' },
      { id: 'star', emoji: '\u2B50', label: 'Star' },
      { id: 'seed', emoji: '\uD83C\uDF31', label: 'Seed' },
      { id: 'blossom', emoji: '\uD83C\uDF38', label: 'Blossom' },
      { id: 'carrot', emoji: '\uD83E\uDD55', label: 'Carrot' },
      { id: 'blueberry', emoji: '\uD83E\uDED0', label: 'Blueberry' },
      { id: 'pray', emoji: '\uD83D\uDE4F', label: 'Pray' },
    ]
  },
  {
    name: 'Primordeia',
    stickers: [
      { id: 'galaxy', emoji: '\uD83C\uDF0C', label: 'Galaxy' },
      { id: 'dna', emoji: '\uD83E\uDDEC', label: 'DNA' },
      { id: 'eye', emoji: '\uD83D\uDC41\uFE0F', label: 'Awareness' },
      { id: 'infinity', emoji: '\u267E\uFE0F', label: 'Infinity' },
      { id: 'dove', emoji: '\uD83D\uDD4A\uFE0F', label: 'Spirit' },
      { id: 'moon', emoji: '\uD83C\uDF19', label: 'Moon' },
      { id: 'sun', emoji: '\u2600\uFE0F', label: 'Sun' },
      { id: 'rainbow', emoji: '\uD83C\uDF08', label: 'Rainbow' },
    ]
  },
  {
    name: 'Feelings',
    stickers: [
      { id: 'mind-blown', emoji: '\uD83E\uDD2F', label: 'Mind Blown' },
      { id: 'peace', emoji: '\u270C\uFE0F', label: 'Peace' },
      { id: 'heart-eyes', emoji: '\uD83D\uDE0D', label: 'Love' },
      { id: 'thinking', emoji: '\uD83E\uDD14', label: 'Thinking' },
      { id: 'grateful', emoji: '\uD83E\uDD79', label: 'Grateful' },
      { id: 'fire', emoji: '\uD83D\uDD25', label: 'Fire' },
      { id: 'crystal', emoji: '\uD83D\uDD2E', label: 'Crystal Ball' },
      { id: 'light', emoji: '\uD83D\uDCA1', label: 'Enlightened' },
    ]
  }
];

function getSupportedMimeType() {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
  ];
  for (const type of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
}

function mimeToExtension(mime) {
  if (mime.includes('webm')) return '.webm';
  if (mime.includes('mp4')) return '.mp4';
  if (mime.includes('ogg')) return '.ogg';
  if (mime.includes('wav')) return '.wav';
  return '.webm';
}

// Renders an assistant message with the next-tier name highlighted as a clickable link.
// Splits the message on the first occurrence of the tier name (case-insensitive) and inserts a button.
function renderWithTierLink(content, tierName, onClick) {
  if (!content || !tierName) return content;
  const idx = content.toLowerCase().indexOf(tierName.toLowerCase());
  if (idx === -1) return content;
  const before = content.slice(0, idx);
  const matched = content.slice(idx, idx + tierName.length);
  const after = content.slice(idx + tierName.length);
  return (
    <>
      {before}
      <button
        type="button"
        onClick={onClick}
        className="font-semibold underline decoration-dotted underline-offset-2 hover:opacity-80 inline-flex items-center gap-1"
        style={{ color: '#7B5EA7', cursor: 'pointer', background: 'transparent', padding: 0 }}
        data-testid="chat-next-tier-link"
        title="Peek at the next tier"
      >
        {matched}
        <Compass size={11} strokeWidth={2.5} />
      </button>
      {after}
    </>
  );
}

const PapayaChat = () => {
  const {
    sessionId,
    blueberries,
    onBreak,
    breakUntil,
    breakRealization,
    updateFromChatResponse,
    returnFromBreak,
    // `subscription` no longer destructured — legacy paywall removed in iter-35; Lotus credits gate everything now
    dailyOracle,
    currentTier,
    allTiers,
    completedGarments,
    credits,
    freeInteractionsLeft,
    accessReason,
    openPaywall,
  } = useContext(EnlightenmentContext);
  const { user } = useContext(AuthContext);

  const nextTierName = currentTier + 1 < (allTiers?.length || 0) ? allTiers[currentTier + 1].name : null;
  const [showInlineRoadAhead, setShowInlineRoadAhead] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  // Iter-44: when true (Date.now() < degradedUntil), the "Papaya is having a
  // moment" banner is shown. Backend sets response.degraded=true when it fell
  // back to canned PAPAYA_WISDOM because the upstream LLM was unavailable.
  const [degradedUntil, setDegradedUntil] = useState(0);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cycleNum, setCycleNum] = useState(0);
  const [blueberryFlash, setBlueberryFlash] = useState(null);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  // TTS engine — see /app/frontend/src/components/chat/useTTS.js for the 2-path playback strategy.
  // The browser-native speechSynthesis fallback was removed in iter-33 to keep Brave's
  // stock voice from replacing Papaya's.
  const {
    audioRef: audioElRef,
    unlockAudio,
    playTTS,
    resumeBlockedAudio,
    stop: stopTTS,
    needsAudioTap,
    loading: ttsLoading,
  } = useTTS({ enabled: ttsEnabled });
  const [breakMessage, setBreakMessage] = useState(null);
  const [graduationData, setGraduationData] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [micError, setMicError] = useState(null);
  const [showStickers, setShowStickers] = useState(false);
  const [stickerCategory, setStickerCategory] = useState(0);
  const [showBlueberryAnim, setShowBlueberryAnim] = useState(false);
  const [showCarrotAnim, setShowCarrotAnim] = useState(false);
  const [firstBlueberryAwarded, setFirstBlueberryAwarded] = useState(() => {
    return localStorage.getItem('firstBlueberryShown') === 'true';
  });
  const [firstCarrotAwarded, setFirstCarrotAwarded] = useState(() => {
    return localStorage.getItem('firstCarrotShown') === 'true';
  });

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Hard-jump to bottom — used right after history loads or chat opens.
  // Uses RAF + a few retries because the parent container may still be sizing.
  const jumpToBottom = useCallback(() => {
    let attempts = 0;
    const tick = () => {
      const el = messagesEndRef.current;
      if (el) {
        try { el.scrollIntoView({ behavior: 'auto', block: 'end' }); } catch { /* ignore */ }
      }
      attempts += 1;
      if (attempts < 5) {
        requestAnimationFrame(() => setTimeout(tick, 80));
      }
    };
    requestAnimationFrame(tick);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, breakMessage, graduationData, scrollToBottom]);

  useEffect(() => {
    if (isOpen && messages.length === 0) loadChatHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      stopAllMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopAllMedia = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (err) { /* already stopped */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const WELCOME_TEXT = "Welcome, seeker. I am Papaya, the white rabbit, and I will guide you home to Primordeia — the garden of paradise. We walk this path together one tier of consciousness at a time. Speak freely with me, and when your words show real integration of what you've learned, I will award you a blueberry. Five blueberries weave themselves into a carrot \uD83E\uDD55 — and each carrot graduates you to the next tier of consciousness work, where deeper teachings, a new song, and a new sacred garment await. Tell me what stirs in you today, dear one.";
  const WELCOME_BACK_TEXT = "Welcome back, dear one. The path is right where you left it. Tell me what stirs in you today \uD83D\uDC30";

  const loadChatHistory = async () => {
    try {
      // Cache-bust to defeat Brave Shields / aggressive proxy caches that may serve stale chat history
      const res = await fetch(`${API}/chat-history/${sessionId}?_=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        // Existing session — prepend a soft "welcome back" greeting (client-only, not persisted)
        // so the user always sees Papaya at the top of the chat after a refresh
        const lastMsg = data.messages[data.messages.length - 1];
        const showWelcomeBack = lastMsg && lastMsg.role !== 'assistant'
          ? false  // last message was theirs and never got answered — don't tease them
          : true;
        const messagesWithGreeting = showWelcomeBack
          ? [{ role: 'assistant', content: WELCOME_BACK_TEXT, timestamp: '_welcome_back_', _ephemeral: true }, ...data.messages]
          : data.messages;
        setMessages(messagesWithGreeting);
        // Hard jump to the LAST message — multiple RAFs to defeat layout/paint timing
        jumpToBottom();
      } else {
        setMessages([{
          role: 'assistant',
          content: WELCOME_TEXT,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch {
      setMessages([{
        role: 'assistant',
        content: WELCOME_TEXT,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Web Audio + persistent-<audio> playback lives in useTTS hook
  // (see /app/frontend/src/components/chat/useTTS.js). The hook exposes: audioRef (bind to <audio>),
  // unlockAudio(), playTTS(textOrB64, isBase64), resumeBlockedAudio(), needsAudioTap.
  // The native speechSynthesis fallback was removed in iter-33 — Brave's stock voice was clashing.

  // ─── Speech-to-text ───
  const startRecording = async () => {
    setMicError(null);
    setShowStickers(false);

    // Check if MediaRecorder is supported
    if (typeof MediaRecorder === 'undefined') {
      setMicError('Your browser does not support audio recording');
      setTimeout(() => setMicError(null), 4000);
      return;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      setMicError('No supported audio format found in this browser');
      setTimeout(() => setMicError(null), 4000);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        clearInterval(recordingTimerRef.current);
        setIsRecording(false);
        setRecordingTime(0);

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioBlob.size < 200) {
          setMicError('Recording too short. Try again.');
          setTimeout(() => setMicError(null), 3000);
          return;
        }

        // Transcribe
        setIsTranscribing(true);
        const ext = mimeToExtension(mimeType);
        const formData = new FormData();
        formData.append('audio', audioBlob, `recording${ext}`);

        try {
          const res = await fetch(`${API}/stt`, { method: 'POST', body: formData });
          const data = await res.json();
          if (data.text && data.text.trim()) {
            setInputMessage(prev => (prev ? prev + ' ' : '') + data.text.trim());
          } else if (data.error) {
            setMicError('Could not transcribe audio. Try again.');
            setTimeout(() => setMicError(null), 3000);
          } else {
            setMicError('No speech detected. Try speaking louder.');
            setTimeout(() => setMicError(null), 3000);
          }
        } catch {
          setMicError('Transcription failed. Check your connection.');
          setTimeout(() => setMicError(null), 3000);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.onerror = () => {
        setMicError('Recording error occurred');
        setIsRecording(false);
        setTimeout(() => setMicError(null), 3000);
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicError('Microphone permission denied. Allow mic access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setMicError('No microphone found. Connect a mic and try again.');
      } else {
        setMicError('Could not access microphone: ' + err.message);
      }
      setTimeout(() => setMicError(null), 5000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // ─── Sticker send ───
  const sendSticker = (sticker) => {
    setShowStickers(false);
    setInputMessage(prev => (prev ? prev + ' ' : '') + sticker.emoji);
  };

  // Play a 2-second preview snippet of the next tier's song right at the carrot moment
  const playTierPreviewChime = useCallback((nextTierIdx) => {
    try {
      const nextTier = allTiers && allTiers[nextTierIdx];
      const url = nextTier?.songUrl;
      if (!url) return;
      const a = new Audio(url);
      a.volume = 0.55;
      const stop = () => { try { a.pause(); a.src = ''; } catch { /* ignore */ } };
      a.addEventListener('timeupdate', () => { if (a.currentTime >= 2) stop(); });
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked, silent */ });
      setTimeout(stop, 2400);
    } catch { /* ignore */ }
  }, [allTiers]);

  // ─── Send message ───
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    unlockAudio();  // Unlock audio context on send click (user-gesture moment) — Brave/Safari fix
    setShowStickers(false);

    const userMsg = { role: 'user', content: inputMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    const msgText = inputMessage;
    setInputMessage("");
    setIsLoading(true);
    setBreakMessage(null);
    setGraduationData(null);

    try {
      // PERF FIX: request chat WITHOUT TTS so the text arrives in ~3s (LLM only).
      // We fire the TTS request in PARALLEL after text arrives — voice catches up 2-5s later.
      // Brave/CF-edge can hold a connection longer than expected on heavy tiers (36 prompt is
      // huge), so the abort window is generous (45s) AND we retry once on transient failures
      // before falling into the slower history-poll recovery path.
      const callChat = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 45000);
        try {
          const res = await fetch(`${API}/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId, message: msgText, include_tts: false }),
            signal: controller.signal,
            cache: 'no-store'
          });
          return res;
        } finally {
          clearTimeout(timeoutId);
        }
      };
      let res;
      try {
        res = await callChat();
      } catch (firstErr) {
        // One quick retry on AbortError / TypeError ("Failed to fetch") — common in Brave
        // when Shields rate-limits the first connection. We sleep ~600ms to let the channel
        // settle, then try again. If it still fails, fall through to the history-poll path.
        await new Promise(r => setTimeout(r, 600));
        res = await callChat();
      }

      // 423 → integration-pause: user tried to chat before the 1-hour break expired.
      // Show a gentle nudge with the realization summary; restore the staged message.
      if (res.status === 423) {
        let detail = {};
        try { detail = (await res.json()).detail || {}; } catch (_e) { /* non-JSON body */ }
        setMessages(prev => prev.filter(m => m !== userMsg));
        setInputMessage(msgText);
        setBreakMessage({
          message: detail.message || "There is no revelation without integration. Take more time.",
          realization_summary: detail.realization_summary || "",
          break_until: detail.break_until || null,
        });
        return;
      }

      // 402 → out of lotuses, surface the paywall and keep their text staged
      if (res.status === 402) {
        // Roll back the local user message so the input is restored for after-purchase retry
        setMessages(prev => prev.filter(m => m !== userMsg));
        setInputMessage(msgText);
        openPaywall();
        return;
      }

      const data = await res.json();

      // Iter-44: backend flags degraded=true when it served canned wisdom
      // because the upstream LLM was unavailable. Show the "Papaya is having
      // a moment" banner for ~15s so the seeker knows to retry once the
      // wider network settles, without silently losing their words.
      if (data.degraded) {
        setDegradedUntil(Date.now() + 15000);
        // Force the banner to fade after the 15s window even if no other
        // interaction triggers a re-render.
        setTimeout(() => setDegradedUntil(t => (Date.now() >= t ? 0 : t)), 15200);
      }

      setMessages(prev => [...prev, {
        role: 'assistant', content: data.response,
        tts_audio: null, // Will be hydrated below when TTS finishes
        timestamp: new Date().toISOString()
      }]);
      setCycleNum(data.cycle_message_num || 0);

      // Fire TTS in parallel — voice arrives 2-5s after text instead of blocking it.
      // Also CACHE the audio onto the message so the replay button is instant.
      if (ttsEnabled && data.response) {
        (async () => {
          try {
            const ttsRes = await fetch(`${API}/tts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: data.response.substring(0, 4096) }),
              cache: 'no-store',
            });
            const tts = await ttsRes.json();
            if (tts.audio) {
              // Hydrate the matching message with the base64 audio for instant replay later
              setMessages(prev => {
                const idx = prev.findLastIndex
                  ? prev.findLastIndex(m => m.role === 'assistant' && m.content === data.response)
                  : (() => { for (let i = prev.length - 1; i >= 0; i--) { if (prev[i].role === 'assistant' && prev[i].content === data.response) return i; } return -1; })();
                if (idx < 0) return prev;
                const copy = prev.slice();
                copy[idx] = { ...copy[idx], tts_audio: tts.audio };
                return copy;
              });
              playTTS(tts.audio, true);
            }
          } catch { /* TTS failure is non-fatal — text already shown */ }
        })();
      }

      updateFromChatResponse(data);

      if (data.blueberry_awarded && data.insight_selected) {
        const insightText = (data.insight_selected && data.insight_selected.message) || '';
        setMessages((prev) => [...prev, {
          role: 'system',
          kind: 'blueberry-award',
          content: `🫐 Blueberry earned — most insightful turn: "${insightText}"`,
          insight_message: insightText,
          insight_reason: (data.insight_selected && data.insight_selected.reason) || '',
          timestamp: new Date().toISOString() + '_blueberry'
        }]);
        if (!firstBlueberryAwarded) {
          setShowBlueberryAnim(true);
          setFirstBlueberryAwarded(true);
          localStorage.setItem('firstBlueberryShown', 'true');
          setTimeout(() => setShowBlueberryAnim(false), 4500);
        }
      }
      if (data.on_break && !data.tier_graduated) setBreakMessage(data.insight_selected);
      if (data.tier_graduated) {
        setGraduationData(data.tier_summary);
        setTimeout(() => playTierPreviewChime(currentTier + 1), 900);
        if (!firstCarrotAwarded) {
          setShowCarrotAnim(true);
          setFirstCarrotAwarded(true);
          localStorage.setItem('firstCarrotShown', 'true');
          setTimeout(() => setShowCarrotAnim(false), 4500);
        }
      }
    } catch {
      // Brave/network may abort the response stream. Poll chat-history a few times.
      // CRITICAL: never call setMessages(histData.messages) blindly — that wipes the user's
      // just-sent text if the backend never received it. Instead, MERGE: keep our local
      // messages and only append history items we don't already have.
      let recovered = false;
      const userMsgTs = userMsg.timestamp;
      for (let attempt = 0; attempt < 6 && !recovered; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, attempt === 1 ? 1500 : 3000));
        try {
          const histRes = await fetch(`${API}/chat-history/${sessionId}?_=${Date.now()}`, {
            cache: 'no-store',
            credentials: 'include',
          });
          if (!histRes.ok) continue;
          const histData = await histRes.json();
          if (!histData.messages || histData.messages.length === 0) continue;
          // Does the history include an assistant reply that came AFTER our user message?
          // Find our user message by content match (timestamps differ between client and server).
          const userIdx = histData.messages.findIndex(m => m.role === 'user' && m.content === msgText);
          if (userIdx < 0) continue; // Backend hasn't saved our message yet
          const replyAfter = histData.messages.slice(userIdx + 1).find(m => m.role === 'assistant');
          if (replyAfter) {
            // Merge: keep our state (which has the user msg already), append only new items from history
            setMessages(prev => {
              const haveAssistant = prev.some(m => m.role === 'assistant' && m.content === replyAfter.content);
              if (haveAssistant) return prev;
              return [...prev, { ...replyAfter, tts_audio: null, timestamp: replyAfter.timestamp || new Date().toISOString() }];
            });
            recovered = true;
            if (ttsEnabled && replyAfter.content) playTTS(replyAfter.content);
            break;
          }
        } catch { /* network blip — try again */ }
        // Preserve userMsgTs for future use without an unused-var warning
        void userMsgTs;
      }
      if (!recovered) {
        // Backend never produced an assistant reply for this turn — drop the staged user
        // bubble (so the chat doesn't carry a "ghost" turn the seeker can't continue from),
        // restore their text into the input box so they can resend with one tap, and surface
        // a kind nudge as a Papaya line. Iter-43: ALSO scrub the half-saved user-msg from
        // chat-history so polling on a future send doesn't pair it with a future reply.
        setMessages(prev => prev.filter(m => m !== userMsg));
        setInputMessage(msgText);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "My ears twitched at the wrong moment, love — the connection wavered. Your words are still here in the box; tap send when you're ready and I'll meet you again. 🐰",
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturn = () => { returnFromBreak(); setBreakMessage(null); setGraduationData(null); setCycleNum(0); };

  const showBreakState = onBreak || breakMessage || graduationData;
  // iter-35: the legacy "Free Trial Ended" subscription gate was removed — the
  // Lotus credit paywall (triggered by 402 from /api/chat) is now the single
  // source of truth. We keep `subscription` in context for analytics but no
  // longer derive a separate `subscriptionExpired` flag from it.
  const inputDisabled = showBreakState || isLoading || isTranscribing;

  return (
    <>
      {/* Persistent <audio> element for Brave-resilient TTS playback. Hidden but lives in the DOM
          for the entire chat lifecycle so its play() promise inherits the gesture grant. */}
      <audio ref={audioElRef} preload="auto" playsInline style={{ display: 'none' }}
        data-testid="papaya-audio-element" />

      {/* Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
            onClick={() => { unlockAudio(); setIsOpen(true); }}
            className="fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center z-50 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)', boxShadow: '0 8px 32px rgba(79, 55, 139, 0.4)' }}
            data-testid="papaya-chat-button">
            <img src="https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png"
              alt="Papaya" className="w-14 h-14 rounded-full object-cover" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: 100, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 w-96 h-[600px] rounded-3xl shadow-2xl flex flex-col z-50 overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 100px)', backgroundColor: '#FAF7F2' }}
            data-testid="papaya-chat-window">

            {/* Header */}
            <ChatHeader
              isRecording={isRecording}
              isTranscribing={isTranscribing}
              cycleNum={cycleNum}
              accessReason={accessReason}
              credits={credits}
              freeInteractionsLeft={freeInteractionsLeft}
              openPaywall={openPaywall}
              ttsEnabled={ttsEnabled}
              setTtsEnabled={setTtsEnabled}
              stopTTS={stopTTS}
              onClose={() => setIsOpen(false)}
            />

            {/* Outfit progress bar — only renders for Level 28 (Holy Soldier) / Level 29 (Primordeia Armor) tiers */}
            <OutfitProgressBar currentTier={currentTier} completedGarments={completedGarments} />

            {/* Low-credit reminder banner — shown when free is gone AND credits ≤ 5 */}
            {accessReason === 'paid' && credits <= 5 && credits > 0 && (
              <button
                type="button"
                onClick={openPaywall}
                className="w-full px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(90deg, #D4A574 0%, #7B5EA7 100%)', color: '#FFF8E7' }}
                data-testid="low-lotus-banner"
              >
                <span>{"\u{1FAB7}"}</span> Only <strong>{credits}</strong> lotus{credits === 1 ? '' : 'es'} left — tap to add more
              </button>
            )}

            {/* Cycle Progress */}
            {cycleNum > 0 && cycleNum < 5 && !showBreakState && (
              <div className="px-4 py-2" style={{ backgroundColor: '#F0E8F8' }}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: '#D8CCE8' }}>
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${(cycleNum / 5) * 100}%`, background: 'linear-gradient(90deg, #4F378B, #D4A574)' }} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: '#4F378B' }}>{cycleNum}/5</span>
                </div>
              </div>
            )}

            {/* Mic Error Banner */}
            <AnimatePresence>
              {micError && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="px-4 py-2 text-xs text-center font-medium"
                  style={{ backgroundColor: '#FDE8E8', color: '#C0392B' }}
                  data-testid="mic-error">
                  {micError}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Iter-44: "Papaya is having a moment" banner — surfaces when the
                backend served canned wisdom because the upstream LLM was
                unavailable. Fades out ~15s after the last degraded response so
                the seeker can retry once the wider network settles. */}
            <AnimatePresence>
              {degradedUntil > Date.now() && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-4 py-2 text-xs text-center font-medium flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(90deg, #FFF8E7 0%, #FBEACB 100%)',
                    color: '#5A3E12',
                    borderTop: '1px solid #D4A574',
                    borderBottom: '1px solid #D4A574',
                  }}
                  data-testid="papaya-degraded-banner"
                >
                  <span style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Papaya is having a moment — the wider garden is wavering. Your words are safe; give her a heartbeat and try again. 🌾
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Audio-unlock Banner (Brave/Safari autoplay fix) */}
            <AnimatePresence>
              {needsAudioTap && ttsEnabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-stretch w-full"
                  data-testid="audio-unlock-banner-wrap"
                >
                  <button
                    type="button"
                    onClick={resumeBlockedAudio}
                    className="flex-1 px-3 py-2 text-xs text-center font-semibold flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(90deg, #4F378B 0%, #7B5EA7 100%)', color: '#FFF' }}
                    data-testid="audio-unlock-banner"
                  >
                    <Volume2 size={14} />
                    Your browser blocked Papaya&rsquo;s voice — tap to unlock
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // One-tap retry — replay the most recent Papaya line through the full TTS pipeline
                      const lastPapaya = [...messages].reverse().find(m => m.role === 'assistant' || m.role === 'papaya');
                      if (!lastPapaya) return;
                      unlockAudio();
                      if (lastPapaya.tts_audio) {
                        playTTS(lastPapaya.tts_audio, true);
                      } else if (lastPapaya.content) {
                        playTTS(lastPapaya.content);
                      }
                    }}
                    className="px-3 py-2 text-xs font-semibold flex items-center justify-center gap-1 border-l border-white/20"
                    style={{ background: 'linear-gradient(90deg, #7B5EA7 0%, #D4A574 100%)', color: '#FFF8E7' }}
                    title="Replay Papaya's last line through her own voice"
                    data-testid="audio-retry-button"
                  >
                    <RefreshCw size={12} />
                    Retry Papaya&rsquo;s voice
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Blueberry Flash */}
            <AnimatePresence>
              {blueberryFlash && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  className="mx-4 mt-2 p-3 rounded-xl text-center"
                  style={{ backgroundColor: '#E8E0F8', border: '1px solid #D4A574' }} data-testid="blueberry-flash">
                  <div className="text-sm font-bold mb-1" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>+1 Blueberry</div>
                  <div className="text-xs" style={{ color: '#5C5470' }}>{blueberryFlash.reason}</div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Messages */}
            <ChatMessages
              messages={messages}
              user={user}
              sessionId={sessionId}
              currentTier={currentTier}
              nextTierName={nextTierName}
              isLoading={isLoading}
              ttsLoading={ttsLoading}
              dailyOracle={dailyOracle}
              showBreakState={showBreakState}
              blueberries={blueberries}
              breakUntil={breakUntil}
              breakRealization={breakRealization}
              breakMessage={breakMessage}
              handleReturn={handleReturn}
              graduationData={graduationData}
              setGraduationData={setGraduationData}
              showBlueberryAnim={showBlueberryAnim}
              setShowBlueberryAnim={setShowBlueberryAnim}
              showCarrotAnim={showCarrotAnim}
              setShowCarrotAnim={setShowCarrotAnim}
              onTierGraduate={(data) => {
                // Refresh session state from server after Level 28 action-item graduation
                if (data && data.session) {
                  updateFromChatResponse({
                    tier: data.session.tier,
                    blueberries: data.session.blueberries,
                    carrots: data.session.carrots,
                    pomegranate_seeds: data.session.pomegranate_seeds,
                    completed_garments: data.session.completed_garments,
                  });
                  setShowCarrotAnim(true);
                  setTimeout(() => setShowCarrotAnim(false), 4500);
                }
              }}
              renderWithTierLink={renderWithTierLink}
              openRoadAhead={() => setShowInlineRoadAhead(true)}
              unlockAudio={unlockAudio}
              playTTS={playTTS}
              messagesEndRef={messagesEndRef}
            />

            {/* Sticker Picker */}
            <AnimatePresence>
              {showStickers && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 180 }} exit={{ opacity: 0, height: 0 }}
                  className="border-t overflow-hidden" style={{ borderColor: '#E8E0F0', backgroundColor: '#FDFBF8' }}
                  data-testid="sticker-picker">
                  {/* Category tabs */}
                  <div className="flex border-b px-2 pt-2" style={{ borderColor: '#E8E0F0' }}>
                    {STICKER_CATEGORIES.map((cat, idx) => (
                      <button key={cat.name} onClick={() => setStickerCategory(idx)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-t-lg transition-colors"
                        style={{
                          backgroundColor: stickerCategory === idx ? '#F0E8F8' : 'transparent',
                          color: stickerCategory === idx ? '#4F378B' : '#8B7DA8'
                        }}>
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  {/* Sticker grid */}
                  <div className="grid grid-cols-4 gap-1 p-2 overflow-y-auto" style={{ maxHeight: 130 }}>
                    {STICKER_CATEGORIES[stickerCategory].stickers.map(sticker => (
                      <motion.button key={sticker.id} whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}
                        onClick={() => sendSticker(sticker)}
                        className="w-full aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 hover:bg-purple-50 transition-colors"
                        title={sticker.label}
                        data-testid={`sticker-${sticker.id}`}>
                        <span className="text-2xl">{sticker.emoji}</span>
                        <span className="text-[9px]" style={{ color: '#8B7DA8' }}>{sticker.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area */}
            <div>
              {/* Recording state */}
              <AnimatePresence>
                {(isRecording || isTranscribing) && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    className="flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg mx-3 mt-2"
                    style={{ backgroundColor: isRecording ? '#FDE8E8' : '#F0E8F8' }}>
                    {isRecording ? (
                      <>
                        <motion.div animate={{ scale: [1, 1.4, 1] }} transition={{ repeat: Infinity, duration: 0.8 }}
                          className="w-3 h-3 rounded-full" style={{ backgroundColor: '#E74C3C' }} />
                        <span className="text-xs font-semibold" style={{ color: '#E74C3C' }}>
                          Recording... {recordingTime}s — tap stop when done
                        </span>
                      </>
                    ) : (
                      <>
                        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                          className="w-3 h-3 rounded-full border-2 border-t-transparent" style={{ borderColor: '#4F378B' }} />
                        <span className="text-xs font-semibold" style={{ color: '#4F378B' }}>Transcribing your speech...</span>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <ChatInput
                inputMessage={inputMessage} setInputMessage={setInputMessage}
                sendMessage={sendMessage} showBreakState={showBreakState}
                inputDisabled={inputDisabled}
                isRecording={isRecording} isTranscribing={isTranscribing}
                startRecording={startRecording} stopRecording={stopRecording}
                showStickers={showStickers} setShowStickers={setShowStickers}
                sendSticker={sendSticker} stickerCategory={stickerCategory}
                setStickerCategory={setStickerCategory}
                STICKER_CATEGORIES={STICKER_CATEGORIES}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline Road Ahead modal — opens when user clicks the next-tier link in chat */}
      <AnimatePresence>
        {showInlineRoadAhead && nextTierName && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center px-4"
            style={{ background: "rgba(20, 14, 32, 0.7)", backdropFilter: "blur(8px)" }}
            onClick={() => setShowInlineRoadAhead(false)}
            data-testid="chat-road-ahead-modal"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 10 }}
              transition={{ type: "spring", damping: 18, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full rounded-3xl p-6"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #F8F4FF 100%)",
                boxShadow: "0 24px 80px rgba(79,55,139,0.4)"
              }}
            >
              <button
                onClick={() => setShowInlineRoadAhead(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-purple-50"
                style={{ color: "#8B7DA8", fontSize: 22 }}
                data-testid="close-chat-road-ahead-modal"
                aria-label="Close"
              >×</button>
              <div className="flex items-center gap-2 mb-4">
                <Compass size={16} style={{ color: "#4F378B" }} />
                <h3 className="text-base font-bold" style={{ color: "#4F378B", fontFamily: "'Fredoka', sans-serif" }}>
                  Papaya pointed to it
                </h3>
              </div>
              <RoadAheadCard nextTierName={nextTierName} variant="full" title="What awaits beyond this tier" />
              <p className="text-xs italic mt-4 text-center" style={{ color: "#8B7DA8", fontFamily: "'Nunito', sans-serif" }}>
                The garment is fogged because it cannot be worn until earned.
                <br />
                Continue speaking with Papaya to walk towards it.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PapayaChat;
